/**
 * Where the sun is overhead right now — enough to draw the day/night line.
 * Standard low-precision solar position (declination + equation of time),
 * accurate to well under a degree, which is far finer than the soft
 * terminator we paint.
 */
export interface SubsolarPoint {
  lat: number;
  lng: number;
}

const RAD = Math.PI / 180;

export function subsolarPoint(date: Date = new Date()): SubsolarPoint {
  const ms = date.getTime();
  // Days since J2000.0 (2000-01-01 12:00 UTC)
  const d = (ms - Date.UTC(2000, 0, 1, 12)) / 86_400_000;
  const g = (357.529 + 0.98560028 * d) * RAD; // mean anomaly
  const q = 280.459 + 0.98564736 * d; // mean longitude
  const L = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * RAD; // ecliptic longitude
  const e = (23.439 - 0.00000036 * d) * RAD; // obliquity
  const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L)); // right ascension
  const dec = Math.asin(Math.sin(e) * Math.sin(L)); // declination
  // Greenwich mean sidereal time, in degrees
  const gmst = (280.46061837 + 360.98564736629 * d) % 360;
  let lng = (ra / RAD - gmst) % 360;
  if (lng > 180) lng -= 360;
  if (lng < -180) lng += 360;
  return { lat: dec / RAD, lng };
}

/** The point directly opposite the sun — centre of the night side. */
export function antisolarPoint(date: Date = new Date()): SubsolarPoint {
  const s = subsolarPoint(date);
  return { lat: -s.lat, lng: s.lng > 0 ? s.lng - 180 : s.lng + 180 };
}

/**
 * Where the moon is overhead (sub-lunar point), from a low-precision lunar
 * theory (mean longitude + the main equation-of-centre term). Good to about
 * a degree — plenty for showing which side of the earth the moon is on.
 */
export function sublunarPoint(date: Date = new Date()): SubsolarPoint {
  const d = (date.getTime() - Date.UTC(2000, 0, 1, 12)) / 86_400_000;
  const L = (218.316 + 13.176396 * d) * RAD; // mean longitude
  const M = (134.963 + 13.064993 * d) * RAD; // mean anomaly
  const F = (93.272 + 13.22935 * d) * RAD; // argument of latitude
  const lon = L + 6.289 * RAD * Math.sin(M); // ecliptic longitude
  const lat = 5.128 * RAD * Math.sin(F); // ecliptic latitude
  const e = (23.439 - 0.00000036 * d) * RAD;
  const ra = Math.atan2(Math.sin(lon) * Math.cos(e) - Math.tan(lat) * Math.sin(e), Math.cos(lon));
  const dec = Math.asin(Math.sin(lat) * Math.cos(e) + Math.cos(lat) * Math.sin(e) * Math.sin(lon));
  const gmst = (280.46061837 + 360.98564736629 * d) % 360;
  let lng = (ra / RAD - gmst) % 360;
  if (lng > 180) lng -= 360;
  if (lng < -180) lng += 360;
  return { lat: dec / RAD, lng };
}

/**
 * Phase angle sun–earth–moon in degrees: 0 = new moon, 180 = full. Uses the
 * directions only (distances do not matter at this level of detail).
 */
export function moonPhaseAngle(sun: SubsolarPoint, moon: SubsolarPoint): number {
  const a = toUnit(sun);
  const b = toUnit(moon);
  const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  return Math.acos(dot) / RAD;
}

function toUnit(p: SubsolarPoint): [number, number, number] {
  const la = p.lat * RAD;
  const lo = p.lng * RAD;
  return [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)];
}

/** The pole of the ecliptic (the plane the moon roughly orbits in), as a point on the earth it stands over right now. */
export function eclipticPole(date: Date = new Date()): SubsolarPoint {
  const d = (date.getTime() - Date.UTC(2000, 0, 1, 12)) / 86_400_000;
  const e = 23.439 - 0.00000036 * d;
  const gmst = (280.46061837 + 360.98564736629 * d) % 360;
  let lng = (270 - gmst) % 360;
  if (lng > 180) lng -= 360;
  if (lng < -180) lng += 360;
  return { lat: 90 - e, lng };
}
